import { LargeSiteSettingsRefs } from "@/tools/create/lss-from-map/components/LargeSiteSettings";
import { PointWithMetadata } from "@/tools/create/lss-from-map/types";
import { Polygon } from "leaflet";
import { MutableRefObject, PropsWithChildren, createContext, useContext, useRef, useState } from "react";
interface LssFromMapTutorialState {
  isTutorialOpen: boolean;
  // Polygon stage
  addressLookup: MutableRefObject<null>;
  mapContainer: MutableRefObject<null>;
  areaIndicator: MutableRefObject<null>;
  polygonNameInput: MutableRefObject<null>;
  generateGridBtn: MutableRefObject<null>;
  // Points stage
  confirmSettingsBtn: MutableRefObject<null>;
  // Settings stage
  settingsContent: MutableRefObject<null>;
  // The inner scrollable region of the settings panel - used to reset scroll position when
  // navigating backward from Financial Settings to Asset Settings (see LssFromMapTutorial.tsx).
  settingsScrollArea: MutableRefObject<HTMLDivElement | null>;
  // Financial Settings renders in two places (narrow Collapse vs. always-visible wide layout),
  // both mounted simultaneously — only one is actually visible at a time via CSS.
  financialSectionNarrow: MutableRefObject<HTMLDivElement | null>;
  financialSectionWide: MutableRefObject<HTMLDivElement | null>;
  proceedToReviewBtn: MutableRefObject<null>;
  settingsRefs: LargeSiteSettingsRefs | null;
  setSettingsRefs: (refs: LargeSiteSettingsRefs) => void;
  // Demo polygon/points, rendered in place of real state while the tour is on the map stages.
  tutorialDemoPolygon: Polygon | null;
  setTutorialDemoPolygon: (polygon: Polygon | null) => void;
  tutorialDemoPoints: PointWithMetadata[];
  setTutorialDemoPoints: (points: PointWithMetadata[]) => void;
  expandFinancialSection: boolean;
  setExpandFinancialSection: (value: boolean) => void;
}
const emptyRef: MutableRefObject<null> = {
  current: null
};
const emptyDivRef: MutableRefObject<HTMLDivElement | null> = {
  current: null
};
const LssFromMapTutorialContext = createContext<LssFromMapTutorialState | undefined>(undefined);
export const LssFromMapTutorialProvider = ({
  isTutorialOpen,
  children
}: PropsWithChildren & {
  isTutorialOpen: boolean;
}) => {
  const addressLookupRef = useRef<null>(null);
  const mapContainerRef = useRef<null>(null);
  const areaIndicatorRef = useRef<null>(null);
  const polygonNameInputRef = useRef<null>(null);
  const generateGridBtnRef = useRef<null>(null);
  const confirmSettingsBtnRef = useRef<null>(null);
  const settingsContentRef = useRef<null>(null);
  const settingsScrollAreaRef = useRef<HTMLDivElement>(null);
  const financialSectionNarrowRef = useRef<HTMLDivElement>(null);
  const financialSectionWideRef = useRef<HTMLDivElement>(null);
  const proceedToReviewBtnRef = useRef<null>(null);
  const [settingsRefs, setSettingsRefs] = useState<LargeSiteSettingsRefs | null>(null);
  const [tutorialDemoPolygon, setTutorialDemoPolygon] = useState<Polygon | null>(null);
  const [tutorialDemoPoints, setTutorialDemoPoints] = useState<PointWithMetadata[]>([]);
  const [expandFinancialSection, setExpandFinancialSection] = useState(false);
  return <LssFromMapTutorialContext.Provider value={{
    isTutorialOpen,
    addressLookup: addressLookupRef,
    mapContainer: mapContainerRef,
    areaIndicator: areaIndicatorRef,
    polygonNameInput: polygonNameInputRef,
    generateGridBtn: generateGridBtnRef,
    confirmSettingsBtn: confirmSettingsBtnRef,
    settingsContent: settingsContentRef,
    settingsScrollArea: settingsScrollAreaRef,
    financialSectionNarrow: financialSectionNarrowRef,
    financialSectionWide: financialSectionWideRef,
    proceedToReviewBtn: proceedToReviewBtnRef,
    settingsRefs,
    setSettingsRefs,
    tutorialDemoPolygon,
    setTutorialDemoPolygon,
    tutorialDemoPoints,
    setTutorialDemoPoints,
    expandFinancialSection,
    setExpandFinancialSection
  }} data-sentry-element="unknown" data-sentry-component="LssFromMapTutorialProvider" data-sentry-source-file="LssFromMapTutorialContext.tsx">
      {children}
    </LssFromMapTutorialContext.Provider>;
};
export const useLssFromMapTutorialState = (): LssFromMapTutorialState => {
  const context = useContext(LssFromMapTutorialContext);
  if (!context) {
    return {
      isTutorialOpen: false,
      addressLookup: emptyRef,
      mapContainer: emptyRef,
      areaIndicator: emptyRef,
      polygonNameInput: emptyRef,
      generateGridBtn: emptyRef,
      confirmSettingsBtn: emptyRef,
      settingsContent: emptyRef,
      settingsScrollArea: emptyDivRef,
      financialSectionNarrow: emptyDivRef,
      financialSectionWide: emptyDivRef,
      proceedToReviewBtn: emptyRef,
      settingsRefs: null,
      setSettingsRefs: () => {},
      tutorialDemoPolygon: null,
      setTutorialDemoPolygon: () => {},
      tutorialDemoPoints: [],
      setTutorialDemoPoints: () => {},
      expandFinancialSection: false,
      setExpandFinancialSection: () => {}
    };
  }
  return context;
};